import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { WizardState } from '../models/wizard-state.model';
import { AiGenerationMode } from '../constants/ai-modes.constants';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MapCreationService {
  private apiUrl = environment.serviceurl + 'api/map-creation/';

  constructor(private http: HttpClient) {}

  getPrefillData(mapId: string, userId: any): Observable<any> {
    return this.http.post(this.apiUrl + 'prefill/', {
      map_id: mapId,
      user_id: userId
    });
  }

  getCreateFullStatus(mapId: string): Observable<any> {
    return this.http.get(this.apiUrl + 'status/' + mapId + '/');
  }

  private buildCommonFormData(state: WizardState, userId: any): { formData: FormData; aiModePayload: string } {
    const formData = new FormData();

    // Map aiMode enum to lowercase expected by the backend view validator
    let aiModePayload = 'manual';
    if (state.aiMode === AiGenerationMode.AiAssisted) {
      aiModePayload = 'ai_assisted';
    } else if (state.aiMode === AiGenerationMode.Guided) {
      aiModePayload = 'guided_generation';
    }

    // Build the mapped global_context payload to align key mismatches
    const globalContext = {
      worldDescription: state.globalContext.description || '',
      primaryThemes: state.globalContext.primaryThemes || '',
      worldCreationType: state.globalContext.creationType || '',
      socialHierarchy: state.globalContext.socialHierarchy || '',
      currencyType: state.globalContext.currencyType || '',
      technologyLevel: state.globalContext.technologyLevel || '',
      magicLevel: (state.globalContext as any).magicLevel || '',
      creationStory: state.globalContext.creationStory || '',
      uniqueFeatures: state.globalContext.uniqueFeatures || '',
      customsHolidays: state.globalContext.customsHolidays || '',
      techInnovations: state.globalContext.techInnovations || '',
      recentEvents: state.globalContext.recentEvents || '',
      politicalOverview: state.globalContext.politicalOverview || '',
      globalConflicts: state.globalContext.globalConflicts || '',
      prominentGuilds: state.globalContext.prominentGuilds || '',
      guildDensity: state.globalContext.guildDensity || '',
      guildCulture: state.globalContext.guildCulture || '',
      guildVolatility: state.globalContext.guildVolatility || ''
    };

    // Compile the unified entity_details collection for Guided Generation mode
    const entityDetails: any[] = [];
    if (state.aiMode === AiGenerationMode.Guided) {
      if (state.cultures) {
        state.cultures.forEach((c: any, index: number) => {
          entityDetails.push({
            entityType: 'culture',
            azgaarId: Number(c.azgaarId || c.azgaar_id || (index + 1)),
            name: c.name,
            temperament: c.temperament || '',
            expansionDrive: c.expansionDrive || '',
            notes: c.notes || ''
          });
        });
      }
      if (state.religions) {
        state.religions.forEach((r: any, index: number) => {
          entityDetails.push({
            entityType: 'religion',
            azgaarId: Number(r.azgaarId || r.azgaar_id || (index + 1)),
            name: r.name,
            zeal: r.zeal || '',
            conversionPressure: r.convertPressure || r.conversionPressure || '',
            notes: r.notes || ''
          });
        });
      }
      if (state.states) {
        state.states.forEach((s: any, index: number) => {
          entityDetails.push({
            entityType: 'state',
            azgaarId: Number(s.azgaarId || s.azgaar_id || (index + 1)),
            name: s.name,
            corruption: s.corruption || '',
            domesticPolicy: s.domesticPolicy || '',
            foreignPolicy: s.foreignPolicy || '',
            notes: s.notes || ''
          });
        });
      }
      if (state.provinces) {
        state.provinces.forEach((p: any, index: number) => {
          entityDetails.push({
            entityType: 'province',
            azgaarId: Number(p.azgaarId || p.azgaar_id || (index + 1)),
            name: p.name,
            stability: p.stability || '',
            prosperity: p.prosperity || '',
            notes: p.notes || ''
          });
        });
      }
    }

    formData.append('user_id', String(userId));
    formData.append('ai_mode', aiModePayload);
    formData.append('user_notes', state.userNotes || '');
    formData.append('map_genre', state.mapGenre || '');

    // Add JSON stringified configurations
    formData.append('resource_config', JSON.stringify(state.resourceConfig));
    formData.append('world_identity', JSON.stringify(state.worldIdentity));
    formData.append('global_context', JSON.stringify(globalContext));
    formData.append('entity_details', JSON.stringify(entityDetails));

    // Chaos levels (for Guided and AI Assisted modes)
    formData.append('culture_chaos_level', state.cultureChaosLevel || '');
    formData.append('religion_chaos_level', state.religionChaosLevel || '');
    formData.append('state_chaos_level', state.stateChaosLevel || '');
    formData.append('province_chaos_level', state.provinceChaosLevel || '');

    return { formData, aiModePayload };
  }

  createWorld(state: WizardState, userId: any): Observable<any> {
    const { formData } = this.buildCommonFormData(state, userId);
    formData.append('map_id', state.mapId || '');
    return this.http.post(this.apiUrl + 'create/', formData);
  }

  createFull(state: WizardState, userId: any): Observable<any> {
    const { formData } = this.buildCommonFormData(state, userId);
    formData.append('map_name', state.mapName || '');
    if (state.mapFile) {
      formData.append('map_file', state.mapFile);
    }
    if (state.jsonFile) {
      formData.append('json_file', state.jsonFile);
    }
    return this.http.post(this.apiUrl + 'create-full/', formData);
  }
}
